import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Search, X } from 'lucide-react';

const emptyInput = { sku: '', name: '', input_type: 'rm', qty_per_unit: 0, unit: '' };

const ProcessMaster = () => {
  const [items, setItems] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const emptyForm = { code: '', name: '', sequence: 0, inputs: [], output_name: '', output_sku: '', output_category: 'wip', is_final: false, description: '' };
  const [formData, setFormData] = useState({ ...emptyForm });
  const [inputRow, setInputRow] = useState({ ...emptyInput });

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const [pRes, iRes, uRes] = await Promise.all([
        api.get('/packaging/processes'), api.get('/sales/inventory'), api.get('/sales/units')
      ]);
      setItems(pRes.data); setInventory(iRes.data); setUnits(uRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setFormData({ ...emptyForm }); setInputRow({ ...emptyInput }); setShowModal(true); };
  const openEdit = (p) => {
    setEditing(p);
    setFormData({
      code: p.code, name: p.name, sequence: p.sequence || 0, inputs: p.inputs || [],
      output_name: p.output_name || '', output_sku: p.output_sku || '',
      output_category: p.output_category || 'wip', is_final: !!p.is_final, description: p.description || '',
    });
    setInputRow({ ...emptyInput });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const { code, ...rest } = formData; // code is immutable key
        await api.put(`/packaging/processes/${editing.id}`, rest);
      } else {
        await api.post('/packaging/processes', formData);
      }
      setShowModal(false); fetchData();
    } catch (err) {
      const d = err.response?.data?.detail;
      alert(typeof d === 'string' ? d : (d ? JSON.stringify(d) : `Failed (${err.response?.status || err.message})`));
    }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Delete process "${code}"?`)) return;
    try { await api.delete(`/packaging/processes/${id}`); fetchData(); } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const onOutputSkuChange = (sku) => {
    const inv = inventory.find(i => i.sku === sku);
    setFormData({ ...formData, output_sku: sku, output_name: inv?.name || formData.output_name, output_category: inv?.product_category === 'fg' ? 'fg' : 'wip', is_final: inv?.product_category === 'fg' });
  };

  const onInputSkuChange = (sku) => {
    const inv = inventory.find(i => i.sku === sku);
    setInputRow({ ...inputRow, sku, name: inv?.name || '', unit: inv?.unit || inputRow.unit, input_type: inv?.product_category === 'wip' ? 'wip' : 'rm' });
  };

  const addInput = () => {
    if (!inputRow.sku && !inputRow.name) { alert('Select a material first'); return; }
    setFormData({ ...formData, inputs: [...formData.inputs, { ...inputRow, qty_per_unit: parseFloat(inputRow.qty_per_unit) || 0 }] });
    setInputRow({ ...emptyInput });
  };
  const removeInput = (idx) => setFormData({ ...formData, inputs: formData.inputs.filter((_, i) => i !== idx) });

  const filtered = items.filter(p => p.name?.toLowerCase().includes(searchTerm.toLowerCase()) || p.code?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div data-testid="process-master-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Process Master</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="add-process-btn"><Plus size={16} /> Add Process</button>
      </div>
      <div className="card mb-4">
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input type="text" className="form-input" placeholder="Search process..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
        </div>
      </div>
      <div className="card">
        {loading ? <div>Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Seq</th><th>Code</th><th>Process</th><th>Inputs</th><th>Output</th><th>Type</th><th>Final?</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={8} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No processes found</td></tr> : filtered.map(p => (
                  <tr key={p.id} data-testid={`process-row-${p.code}`}>
                    <td>{p.sequence}</td>
                    <td className="font-mono">{p.code}</td>
                    <td>{p.name}</td>
                    <td className="text-sm">{(p.inputs || []).map(i => i.name).join(', ') || '-'}</td>
                    <td>{p.output_name || '-'} {p.output_sku ? <span className="text-xs text-muted">({p.output_sku})</span> : ''}</td>
                    <td><span className={`badge ${p.output_category === 'fg' ? 'badge-success' : 'badge-warning'}`}>{p.output_category === 'fg' ? 'Finished' : 'Semi-Finished'}</span></td>
                    <td>{p.is_final ? <span className="badge badge-info">Final</span> : '-'}</td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(p)} className="btn btn-ghost btn-sm" data-testid={`edit-btn-${p.code}`}><Edit size={14} /></button>
                        <button onClick={() => handleDelete(p.id, p.code)} className="btn btn-ghost btn-sm" data-testid={`delete-btn-${p.code}`}><Trash2 size={14} color="#DC2626" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="process-modal">
          <div className="modal-content" style={{ maxWidth: '760px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? 'Edit Process' : 'Create Process'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-3">
                  <div className="form-group"><label className="form-label">Code *</label><input type="text" className="form-input" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })} required disabled={!!editing} placeholder="e.g. printing" data-testid="code-input" /></div>
                  <div className="form-group"><label className="form-label">Name *</label><input type="text" className="form-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="name-input" /></div>
                  <div className="form-group"><label className="form-label">Sequence</label><input type="number" className="form-input" value={formData.sequence} onChange={(e) => setFormData({ ...formData, sequence: parseInt(e.target.value) || 0 })} /></div>
                </div>
                <div className="grid grid-cols-3">
                  <div className="form-group">
                    <label className="form-label">Output Item (SKU)</label>
                    <select className="form-select" value={formData.output_sku} onChange={(e) => onOutputSkuChange(e.target.value)} data-testid="output-sku-select">
                      <option value="">Select output...</option>
                      {inventory.filter(i => i.product_category !== 'rm').map(i => <option key={i.sku} value={i.sku}>{i.sku} - {i.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Output Name</label><input type="text" className="form-input" value={formData.output_name} onChange={(e) => setFormData({ ...formData, output_name: e.target.value })} /></div>
                  <div className="form-group">
                    <label className="form-label">Output Type</label>
                    <select className="form-select" value={formData.output_category} onChange={(e) => setFormData({ ...formData, output_category: e.target.value })}>
                      <option value="wip">Semi-Finished (WIP)</option>
                      <option value="fg">Finished Good</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-4" style={{ alignItems: 'center', marginBottom: '8px' }}>
                  <label className="flex items-center gap-2" style={{ fontSize: '13px' }}>
                    <input type="checkbox" checked={formData.is_final} onChange={(e) => setFormData({ ...formData, is_final: e.target.checked })} data-testid="is-final-check" />
                    This process produces a final (sellable) product
                  </label>
                </div>

                <label className="form-label" style={{ fontWeight: 600 }}>Input Materials (consumed per unit of output)</label>
                <div className="flex gap-2" style={{ marginBottom: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: 2, minWidth: '160px' }}>
                    <select className="form-select" value={inputRow.sku} onChange={(e) => onInputSkuChange(e.target.value)} data-testid="input-sku-select">
                      <option value="">Select material...</option>
                      {inventory.map(i => <option key={i.sku} value={i.sku}>{i.sku} - {i.name} [{i.product_category}]</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: '90px' }}>
                    <select className="form-select" value={inputRow.input_type} onChange={(e) => setInputRow({ ...inputRow, input_type: e.target.value })}>
                      <option value="rm">Raw Material</option><option value="wip">Semi-Finished</option>
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: '80px' }}><input type="number" step="0.0001" className="form-input" placeholder="Qty/unit" value={inputRow.qty_per_unit || ''} onChange={(e) => setInputRow({ ...inputRow, qty_per_unit: e.target.value })} data-testid="input-qty" /></div>
                  <div style={{ flex: 1, minWidth: '70px' }}>
                    <select className="form-select" value={inputRow.unit} onChange={(e) => setInputRow({ ...inputRow, unit: e.target.value })}>
                      <option value="">Unit</option>
                      {units.map(u => <option key={u.id} value={u.symbol}>{u.symbol}</option>)}
                    </select>
                  </div>
                  <button type="button" onClick={addInput} className="btn btn-secondary btn-sm" data-testid="add-input-btn"><Plus size={14} /></button>
                </div>
                {formData.inputs.length > 0 && (
                  <div className="table-container" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                    <table>
                      <thead><tr><th>Material</th><th>Type</th><th>Qty/unit</th><th>Unit</th><th></th></tr></thead>
                      <tbody>
                        {formData.inputs.map((s, i) => (
                          <tr key={i}><td>{s.name}</td><td>{s.input_type}</td><td>{s.qty_per_unit}</td><td>{s.unit}</td><td><button type="button" onClick={() => removeInput(i)} className="btn btn-ghost btn-sm"><X size={14} color="#DC2626" /></button></td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="form-group" style={{ marginTop: '12px' }}><label className="form-label">Description</label><textarea className="form-textarea" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-process-btn">{editing ? 'Update' : 'Create Process'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessMaster;
